import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  useTheme,
  IconButton,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import FormTextField from "../common/form/FormTextField";
import { OrderProduct } from "../../../models/Order/OrderProduct";
import AppButton from "../common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react";

interface RaiseStockCheckReqPopupProps {
  data: OrderProduct[];
  open: boolean;
  onClose: () => void;
  onSave: (
    products: { product_id: number; notes: string; name: string }[]
  ) => void;
}

const RaiseStockCheckReqPopup = ({
  data,
  open,
  onClose,
  onSave,
}: RaiseStockCheckReqPopupProps) => {
  const theme = useTheme();

  const [productNotes, setProductNotes] = useState<
    { product_id: number; notes: string; error: boolean; name: string }[]
  >([]);

  useEffect(() => {
    setProductNotes(
      data.map((product) => ({
        product_id: product.id,
        name: product.name!,
        notes: "",
        error: false,
      }))
    );
  }, [data]);

  const handleInputChange = (id: number, value: string) => {
    setProductNotes((prevNotes) =>
      prevNotes.map((note) =>
        note.product_id === id ? { ...note, notes: value, error: false } : note
      )
    );
  };

  const handleSave = () => {
    let hasError = false;

    const updatedNotes = productNotes.map((note) => {
      if (!note.notes.trim()) {
        hasError = true;
        return { ...note, error: true };
      }
      return note;
    });

    setProductNotes(updatedNotes);

    if (!hasError) {
      onSave(
        updatedNotes.map(({ product_id, notes, name }) => ({
          product_id,
          notes,
          name,
        }))
      );
      handleClearAll();
      onClose();
    }
  };

  const handleClearAll = () => {
    setProductNotes((prevNotes) =>
      prevNotes.map((note) => ({ ...note, notes: "", error: false }))
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 4,
          pb: 2,
          pt: 2,
        }}
      >
        <CustomTypography
          sx={{ pb: "0px" }}
          style={{ paddingBottom: "0px" }}
          variant="heading4"
        >
          Raise Stock Check Request
        </CustomTypography>
        <IconButton sx={{ left: "12px" }} aria-label="close" onClick={()=>{
          handleClearAll()
          onClose()
        }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 4, pb: 1 }}>
        {productNotes?.map((product) => (
          <Box key={product.product_id}>
            <CustomTypography
              variant="body1"
              fontWeight={500}
              style={{
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Product {product.name}
            </CustomTypography>
            <FormTextField
              label="Write Product Notes"
              required={true}
              multiline
              sx={{ mt: 1, mb: 2 }}
              rows={3}
              value={product.notes}
              onChange={(id, e) => handleInputChange(product.product_id, e!)}
              error={product.error}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions
        sx={{ p: 4, pt: 0, display: "flex", justifyContent: "space-between" }}
      >
        <AppButton
          onClick={handleClearAll}
          label="Clear All"
          variant="grayscale"
        />

        <AppButton onClick={handleSave} label="Confirm" variant="primary" />
      </DialogActions>
    </Dialog>
  );
};

export default observer(RaiseStockCheckReqPopup);
